import { useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getAdminSaleEvent, deleteSaleEvent, type AdminSaleEvent } from '../lib/api'
import { useConfirm } from '../components/ConfirmDialog'

export type SaleEventDetailContext = {
  event: AdminSaleEvent
  reload: () => Promise<void>
}

const TABS = [
  { to: 'overview', label: 'Overview' },
  { to: 'phases', label: 'Phases' },
  { to: 'orders', label: 'Orders' },
  { to: 'checkin', label: 'Checkin' },
]

export default function SaleEventDetailLayout() {
  const { id } = useParams<{ id: string }>()
  const { token } = useAuth()
  const navigate = useNavigate()
  const { confirm, dialog: confirmDialog } = useConfirm()

  const [event, setEvent] = useState<AdminSaleEvent | null>(null)
  const [loading, setLoading] = useState(true)

  async function load() {
    if (!id || !token) return
    setLoading(true)
    try {
      setEvent(await getAdminSaleEvent(token, id))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [id, token])

  async function handleDelete() {
    if (!event || !token || !id) return
    const ok = await confirm(
      `"${event.title}" akan dihapus permanen dan tidak bisa dipulihkan.`,
      { title: `Hapus "${event.title}"`, confirmLabel: 'Hapus' }
    )
    if (!ok) return
    await deleteSaleEvent(token, id)
    navigate('/sales/sale-events')
  }

  if (loading) return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  if (!event) return <div className="p-6"><p className="text-zinc-500 text-sm">Event tidak ditemukan.</p></div>

  return (
    <div>
      {confirmDialog}

      {/* Sticky event header */}
      <div className="sticky top-0 z-10 bg-zinc-900 border-b border-zinc-800">
        <div className="px-6 pt-5 pb-0">

          {/* Back + title + actions row */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <button
                onClick={() => navigate('/sales/sale-events')}
                className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-2"
              >
                <ChevronLeft size={14} />
                Sale Events
              </button>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-white">{event.title}</h1>
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                  event.is_published ? 'bg-green-900 text-green-400' : 'bg-zinc-800 text-zinc-500'
                }`}>
                  {event.is_published ? 'Published' : 'Draft'}
                </span>
              </div>
              <p className="text-zinc-500 text-sm mt-0.5">
                {[event.date, event.time, event.location].filter(Boolean).join(' · ')}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0 mt-1">
              <button
                onClick={() => navigate(`/sales/sale-events/${id}/edit`)}
                className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800 border border-zinc-700"
              >
                Edit Event
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm text-red-500 hover:text-red-400 transition-colors rounded-lg hover:bg-zinc-800/50 border border-zinc-800"
              >
                Hapus
              </button>
            </div>
          </div>

          {/* Sub-nav tabs */}
          <div className="flex items-center gap-1">
            {TABS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    isActive
                      ? 'text-white border-yellow-500'
                      : 'text-zinc-500 border-transparent hover:text-zinc-300'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      {/* Child page content */}
      <Outlet context={{ event, reload: load } satisfies SaleEventDetailContext} />
    </div>
  )
}
